import { useMemo, type ReactNode } from 'react';
import {
  buildPassportView,
  type PassportViewBlock,
  type PassportViewItem,
  type ViewCell,
} from '../model';
import {
  BlockEmpty,
  BlockHeading,
  BlockNav,
  BlockNavButton,
  BlockNavList,
  BlockNote,
  BlockSection,
  KvGroupTitle,
  KvList,
  KvRow,
  KvSubRow,
  Matrix,
  MatrixBox,
  blockAnchor,
  blockNumber,
  jumpToBlock,
} from './passportParts';
import { ScrollRegion } from './ScrollRegion';

/** Значение с подсказкой точной записи, если показ его округлил. */
function Value({ cell }: { cell: ViewCell }) {
  return cell.exact ? (
    <span title={`Точное значение: ${cell.exact}`}>{cell.text}</span>
  ) : (
    <>{cell.text}</>
  );
}

export function PassportBlockNav({
  blocks,
  idPrefix,
  alerts,
}: {
  blocks: readonly { id: string; title: string }[];
  idPrefix: string;
  /** Блоки с ошибками ввода — подсвечены в навигации. */
  alerts?: ReadonlySet<string>;
}) {
  return (
    <BlockNav aria-label="Блоки паспорта">
      <BlockNavList>
        {blocks.map((b, i) => (
          <li key={b.id}>
            <BlockNavButton
              type="button"
              data-alert={alerts?.has(b.id) ? 'true' : undefined}
              onClick={() => jumpToBlock(idPrefix, b.id)}
            >
              <span className="n" aria-hidden="true">
                {blockNumber(i)}
              </span>
              {b.title}
            </BlockNavButton>
          </li>
        ))}
      </BlockNavList>
    </BlockNav>
  );
}

function MatrixView({ item }: { item: Extract<PassportViewItem, { kind: 'matrix' }> }) {
  return (
    <MatrixBox>
      <ScrollRegion label={item.caption}>
        <Matrix>
          <caption>{item.caption}</caption>
          <thead>
            <tr>
              <th scope="col">{item.corner}</th>
              {item.columns.map((c) => (
                <th key={c.key} scope="col">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {item.rows.map((r) => (
              <tr key={`${r.code}-${r.label}`}>
                <th scope="row">{r.label}</th>
                {r.cells.map((cell, i) =>
                  cell ? (
                    <td key={item.columns[i]?.key ?? i}>
                      <Value cell={cell} />
                    </td>
                  ) : (
                    <td key={item.columns[i]?.key ?? i} data-empty="true">
                      —
                    </td>
                  ),
                )}
              </tr>
            ))}
          </tbody>
        </Matrix>
      </ScrollRegion>
    </MatrixBox>
  );
}

/** Подряд идущие поля — одним списком; таблицы и группы — отдельно. */
function BlockItems({ items }: { items: readonly PassportViewItem[] }) {
  const out: ReactNode[] = [];
  let run: Extract<PassportViewItem, { kind: 'field' }>[] = [];
  const flush = () => {
    if (run.length === 0) return;
    const rows = run;
    out.push(
      <KvList key={`kv-${rows[0]?.code ?? out.length}`}>
        {rows.map((it) => (
          <KvRow key={it.code}>
            <dt>{it.label}</dt>
            <dd data-multiline={it.multiline ? 'true' : undefined}>
              <Value cell={it.value} />
            </dd>
          </KvRow>
        ))}
      </KvList>,
    );
    run = [];
  };
  for (const it of items) {
    if (it.kind === 'field') {
      run.push(it);
      continue;
    }
    flush();
    if (it.kind === 'matrix') {
      out.push(<MatrixView key={`m-${it.id}`} item={it} />);
    } else {
      out.push(
        <div key={`g-${it.code}`}>
          <KvGroupTitle>{it.label}</KvGroupTitle>
          <KvList>
            {it.pairs.map((p) => (
              <KvSubRow key={p.key}>
                <dt>{p.label}</dt>
                <dd>
                  <Value cell={p.value} />
                </dd>
              </KvSubRow>
            ))}
          </KvList>
        </div>,
      );
    }
  }
  flush();
  return <>{out}</>;
}

function Block({
  block,
  index,
  idPrefix,
}: {
  block: PassportViewBlock;
  index: number;
  idPrefix: string;
}) {
  const headingId = `${blockAnchor(idPrefix, block.id)}-h`;
  return (
    <BlockSection id={blockAnchor(idPrefix, block.id)} aria-labelledby={headingId}>
      <BlockHeading id={headingId} tabIndex={-1}>
        <span className="n" aria-hidden="true">
          {blockNumber(index)}
        </span>
        {block.title}
      </BlockHeading>
      {block.id === 'other' && (
        <BlockNote>Поля, которых нет в схеме паспорта, — показаны с кодом поля.</BlockNote>
      )}
      {block.items.length === 0 ? (
        <BlockEmpty>Не заполнено</BlockEmpty>
      ) : (
        <BlockItems items={block.items} />
      )}
    </BlockSection>
  );
}

/**
 * Паспорт модели по блокам схемы: навигация по блокам, заполненные поля,
 * таблицы по зонам и годам. Пустой блок не прячется — видно, что не заполнено.
 */
export function PassportView({
  passport,
  idPrefix,
}: {
  passport: Record<string, unknown>;
  idPrefix: string;
}) {
  const blocks = useMemo(() => buildPassportView(passport), [passport]);
  return (
    <>
      <PassportBlockNav blocks={blocks} idPrefix={idPrefix} />
      {blocks.map((b, i) => (
        <Block key={b.id} block={b} index={i} idPrefix={idPrefix} />
      ))}
    </>
  );
}
